import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmtINR } from "@/data/catalog";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | error
  const [amount, setAmount] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= 8) { setState("error"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setAmount(data.amount);
        if (data.payment_status === "paid") { setState("paid"); clear(); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch { if (!cancelled) setTimeout(poll, 2000); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-24 mx-auto max-w-xl px-5 text-center">
      {state === "checking" && (<><Loader2 size={48} className="mx-auto animate-spin text-royal" /><h1 className="mt-6 font-heading text-3xl font-extrabold text-foreground">Confirming your payment…</h1><p className="mt-2 text-muted-foreground">Please wait a moment.</p></>)}
      {state === "paid" && (<><span className="grid place-items-center h-20 w-20 rounded-full bg-emerald/15 text-emerald mx-auto"><CheckCircle2 size={44} /></span><h1 className="mt-6 font-heading text-4xl font-extrabold text-foreground" data-testid="payment-paid">Payment successful!</h1><p className="mt-3 text-muted-foreground">Thank you for your order{amount ? ` of ${fmtINR(amount)}` : ""}. Our team will contact you to confirm production and delivery details.</p><Link to="/products" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 font-semibold hover:bg-royal transition-colors">Continue shopping <ArrowRight size={18} /></Link></>)}
      {(state === "failed" || state === "error") && (<><span className="grid place-items-center h-20 w-20 rounded-full bg-destructive/10 text-destructive mx-auto"><XCircle size={44} /></span><h1 className="mt-6 font-heading text-3xl font-extrabold text-foreground">Payment not confirmed</h1><p className="mt-3 text-muted-foreground">We couldn't confirm your payment. If money was deducted it will be auto-refunded. Please try again or request a quote.</p><Link to="/cart" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 font-semibold">Back to cart</Link></>)}
    </div>
  );
}
